import React from 'react';

import { WidgetView } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { useConfigurationStore } from 'store/configurationsStore';

import { PeriodItemEntry } from 'api/queries/getSummaryTrackedActivities.query';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { CustomTableColumnsModal } from 'components/common/Modals/CustomTableColumns/customTableColumnsModal';
import NoData from 'components/common/NoData/noData';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { ITableWidgetWrapperProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { useSummaryTrackedActivitiesComponentLogic } from 'components/widgets/SummaryTrackedActivities/summaryTrackedActivitiesComponent.logic';
import { useSummaryTrackedActivitiesComponentProvider } from 'components/widgets/SummaryTrackedActivities/summaryTrackedActivitiesComponent.provider';
import {
    columnsV8,
    initialState,
} from 'components/widgets/SummaryTrackedActivities/summaryTrackedActivitiesComponent.utils';

export const SummaryTrackedActivitiesComponent = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    localFilters,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,
    widgetViewMode = WidgetView.page,
}: ITableWidgetWrapperProps) => {
    const { componentKey, routeId: id, endpoints, name } = useConfigurationStore().mySummaryTrackedActivities;

    const { subTitle, route } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title: name,
        setSubTitleForExport,
    });

    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
    });

    const { api, ui } = useSummaryTrackedActivitiesComponentProvider({
        projectId,
        latestVersionId,
        globalFilterData,
        localFilters,
        contractId,
        widgetId,
        compareVersionId,
        table,
        endpoints,
    });

    const { logic } = useSummaryTrackedActivitiesComponentLogic({ api });

    return (
        <WidgetWithTitle
            title={<WidgetTitle title={name} widgetViewMode={widgetViewMode} postfix={logic.counter} />}
            id={id}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId && ui.isInPinboard && ui.pills.length > 0 ? (
                    <PinboardWidgetPills key={'widgetFilters'} filters={ui.pills} />
                ) : null,
            ]}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={name}
                    projectId={projectId}
                    contractId={contractId}
                    elementId={id}
                    widgetId={widgetId}
                    route={route}
                    localFilters={ui.state}
                    callBack={table.setWidgetColumns}
                    shouldHide={true}
                />,
                <ExportDropdown title={name} key={'export'} subTitle={subTitle} />,
                <ColumnsManager
                    onOpenModal={() => table.setModalColumnsOpen(true)}
                    onHideModal={() => table.setModalColumnsOpen(false)}
                    showModal={table.modalColumnsOpen}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    tableInstance={table.tableInstance}
                    initialState={initialState}
                    columns={columnsV8}
                    onChangeOrder={table.setColumnOrder}
                    onChangeVisibility={table.setColumnVisibility}
                />,
                <TooltipIcon key={'info'} tooltip={api.getTrackedActivities.data?.info} />,
                <ThreeDotsMenu
                    widgetViewMode={widgetViewMode}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    shareTitle={name}
                    shareLink={route}
                />,
            ]}
        >
            <TableContainer>
                {table.updatedInitialState && (
                    <TableV8<PeriodItemEntry>
                        data={logic.activeData}
                        columns={columnsV8}
                        isLoading={api.getTrackedActivities.isLoading || api.getTrackedActivities.isFetching}
                        noData={<NoData />}
                        sorting={table.sorting}
                        setSorting={table.setSorting}
                        columnVisibility={table.columnVisibility}
                        setColumnVisibility={table.setColumnVisibility}
                        columnOrder={table.columnOrder}
                        setColumnOrder={table.setColumnOrder}
                        manualPagination={false}
                        manualSorting={false}
                        rowSelection={table.rowSelection}
                        setRowSelection={table.setRowSelection}
                    />
                )}
            </TableContainer>

            <CustomTableColumnsModal
                onHideModal={() => table.setModalColumnsOpen(false)}
                showModal={table.modalColumnsOpen}
                widgetId={widgetId}
                componentKey={componentKey}
                tableInstance={table.tableInstance}
                initialState={initialState}
                columns={columnsV8}
                onChangeOrder={table.setColumnOrder}
                onChangeVisibility={table.setColumnVisibility}
            />
        </WidgetWithTitle>
    );
};
